import type { ReactNode } from 'react';
import React from 'react';

import { twMerge } from 'tailwind-merge';

import { Dialog } from '@headlessui/react';

import { TbAlertTriangle } from 'react-icons/tb';

import type { IconTypes } from 'components-new/icon';
import Icon from 'components-new/icon/icon';
import Typography from 'components-new/typography/typography';
import type Button from 'components-new/button/button';

type ModalVariants = 'neutral' | 'primary' | 'destructive';

interface IFooter {
  helpText?: string;
  primaryButton?: React.ReactElement<typeof Button>;
  secondaryButton?: React.ReactElement<typeof Button>;
  icon?: IconTypes;
}

interface ModalProps {
  isOpen?: boolean;
  onClose: () => void;
  title: string;
  footer?: IFooter;
  variant?: ModalVariants;
  icon?: IconTypes;
  className?: string;
  containerClassName?: string;
  dialogPanelClassName?: string;
  dialogcontentContainerClassName?: string;
  backdropClassName?: string;
  children?: ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  footer,
  variant = 'neutral',
  icon,
  className,
  containerClassName,
  dialogPanelClassName,
  dialogcontentContainerClassName,
  backdropClassName,
  children,
  ...other
}) => {
  const backdropClassNames = twMerge(
    'fixed inset-0',
    'bg-other-overlay bg-opacity-70',
    'backdrop-blur-[3px]',
    backdropClassName,
  );

  const containerClassNames = twMerge(
    'fixed inset-0 w-screen h-screen overflow-y-scroll z-50',
    containerClassName,
  );

  const dialogPanelClassNames = twMerge(
    'flex flex-col overflow-hidden',
    'self-end sm:self-center m-2 sm:m-0',
    'bg-white border border-gray100 rounded-2xl',
    dialogPanelClassName,
  );

  const dialogContentContainerClassNames = twMerge(
    'flex flex-col h-full sm:w-[634px]',
    dialogcontentContainerClassName,
  );

  const iconContainerClassName = twMerge(
    'rounded-full p-2 sm:self-start',
    variant === 'neutral' && 'bg-primary-100 text-primary-400',
    variant === 'primary' && 'bg-primary-100 text-primary-400',
    variant === 'destructive' && 'bg-destructive-100 text-destructive-400',
  );

  const bottomBarClassName = twMerge(
    'border-t border-gray100 px-6 py-3 sm:py-4',
    (variant === 'neutral' || variant === 'primary') &&
      'bg-[#F0F0F0A3] bg-opacity-60',
    variant === 'destructive' && 'bg-[#FCF0F0A3] bg-opacity-60',
  );

  const defaultDialogContent = (
    <>
      <div className="flex flex-col flex-1 sm:flex-row gap-4 items-center py-5 px-6">
        {icon && (
          <div className={iconContainerClassName}>
            <Icon icon={icon} size={32} />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Typography
            variant="heading-2"
            as="span"
            className="text-gray800 text-center sm:text-start"
          >
            {title}
          </Typography>
          {children}
        </div>
      </div>
      <div className={bottomBarClassName}>
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 sm:gap-5">
          {footer?.helpText && (
            <div className="flex-1 flex gap-1 items-center text-gray-600">
              <Icon icon={footer?.icon || TbAlertTriangle} size={16} />
              <Typography variant="body-medium" as="span">
                {footer.helpText}
              </Typography>
            </div>
          )}
          {footer?.secondaryButton}
          {footer?.primaryButton}
        </div>
      </div>
    </>
  );

  return (
    <Dialog onClose={onClose} open={isOpen} className={className} {...other}>
      <div className={backdropClassNames} />
      <div className={containerClassNames}>
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel className={dialogPanelClassNames}>
            <div className={dialogContentContainerClassNames}>
              {defaultDialogContent}
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

export default Modal;
